import { faComments } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EditGlyph } from '@wfp/icons-react';
import { Link, Text } from '@wfp/react';
import React, { Component, useEffect } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { SHOW } from 'src/components/DisplayWrapper';
import { sendSignalsAction } from 'src/redux/componentSignals/actions';
import { clearApiKeys, requestAPI } from 'src/redux/data/actions';
import { selectSection } from 'src/redux/view/actions';
import {
  APPLY_CHANGE_TITLE_KEY,
  COMMENTS_LIST,
  COMMENTS_TARGET_ID,
  REPORT_DETAIL_DATA_KEY,
} from 'src/redux/view/constants';

const SidebarContainer = props => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    handleRefresh();
    // eslint-disable-next-line
  }, []);

  function selectLink(item) {
    const newTitle = props.newTitle.data.new_title;
    const title =
      newTitle && item.custom_title === 'Extra Section'
        ? newTitle
        : item.current_title;

    // Check if the URL hash matches this section title
    const currentHash = decodeURIComponent(location.hash.slice(1));
    if (title === currentHash) {
      handleSelection(item.section_pk);
    }
  }

  useEffect(() => {
    if (!props.activeSectionID && !!props.data.sections.length) {
      // If there's a hash in the URL, try to find and select the matching section
      if (location.hash) {
        const hashTitle = decodeURIComponent(location.hash.slice(1));
        let foundSection = null;

        const findSectionByTitle = (sections, targetTitle) => {
          for (const section of sections) {
            const newTitle = props.newTitle.data.new_title;
            const title =
              newTitle &&
              ['Country office story', 'Extra Section'].includes(
                section.custom_title,
              )
                ? newTitle
                : section.current_title;

            if (title === targetTitle) {
              return section;
            }

            if (section.children_list) {
              const found = findSectionByTitle(
                section.children_list,
                targetTitle,
              );
              if (found) return found;
            }
          }
          return null;
        };

        foundSection = findSectionByTitle(props.data.sections, hashTitle);
        if (foundSection) {
          handleSelection(foundSection.section_pk);
          return;
        }
      }

      // Fallback to first section if no hash or hash not found
      if (!location.hash) {
        handleSelection(props.data.sections[0].section_pk);
      }

      // Legacy code for handling sections (keeping for backward compatibility)
      props.data.sections.forEach(item => {
        selectLink(item);
        if (item.children_list) {
          item.children_list.forEach(chItem => {
            selectLink(chItem);
          });
        }
      });
    }
  }, [
    props.activeSectionID,
    props.data.sections,
    location.hash,
    props.newTitle,
  ]);

  function handleRefresh() {
    props.dispatchRequestData({
      url_format: { _year_: props.year, _cpb_: props.cpb },
    });
  }

  function handleSelection(id) {
    // Clear comments data before selecting new section
    props.dispatchClearComments();

    props.dispatchSelect(id);

    // Find the section data to get the title for URL hash
    const findSectionById = (sections, targetId) => {
      for (const section of sections) {
        if (section.section_pk === targetId) {
          return section;
        }
        if (section.children_list) {
          const found = findSectionById(section.children_list, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const selectedSection = findSectionById(props.data.sections, id);
    if (selectedSection) {
      const newTitle = props.newTitle.data.new_title;
      const title =
        newTitle &&
        ['Country office story', 'Extra Section'].includes(
          selectedSection.custom_title,
        )
          ? newTitle
          : selectedSection.current_title;

      // Update URL with the section hash
      const encodedTitle = encodeURIComponent(title);
      navigate({ hash: encodedTitle }, { replace: true });
    }
  }

  function buildNavigationTreeProps(item, level = 0) {
    const newTitle = props.newTitle.data.new_title;
    const activeSectionID = props.activeSectionID;
    const title =
      newTitle &&
      ['Country office story', 'Extra Section'].includes(item.custom_title)
        ? newTitle
        : item.current_title;

    const onSelect =
      !!item.only_title || item.section_pk === activeSectionID
        ? undefined
        : () => handleSelection(item.section_pk);

    const children = item.children_list
      ? item.children_list.map(item =>
          buildNavigationTreeProps(item, level + 1),
        )
      : [];

    return {
      title: title,
      iAmEditor: item.iAmEditor,
      onSelect,
      onlyTitle: item.only_title,
      onExpandingToggle: undefined,
      expanded: true,
      isActive: item.section_pk === activeSectionID,
      sectionPk: item.section_pk,
      children: children,
      commentsCount: item.comments_count,
    };
  }

  const { data: { sections = [] } = {} } = props;
  const presentationProps = sections.map(buildNavigationTreeProps);

  return (
    <aside className="report-sidebar" data-test-id="report-sidebar">
      <SectionNavigation
        data={presentationProps}
        dispatchOpenComments={props.dispatchOpenComments}
        commentsLoading={props.commentsLoading}
      />
    </aside>
  );
};

export function IAmEditorIcon() {
  return (
    <>
      <EditGlyph className="edit-icon" />
      <Text className="note">You are editing this section</Text>
    </>
  );
}

export function CommentsCount(props) {
  const { useEffect, useRef } = React;
  const targetSectionRef = useRef(null);

  const helpText = `${props.count} open comment${
    props.count > 1 ? 's' : ''
  } in the section`;

  // Open sidebar when target section is active and comments are loaded
  useEffect(() => {
    if (
      targetSectionRef.current === props.sectionPk &&
      props.isActive &&
      !props.commentsLoading
    ) {
      targetSectionRef.current = null;

      if (props.dispatchOpenComments) {
        props.dispatchOpenComments();
      }
    }
  }, [
    props.commentsLoading,
    props.isActive,
    props.sectionPk,
    props.dispatchOpenComments,
  ]);

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();

    if (props.isActive) {
      props.dispatchOpenComments?.();
    } else {
      targetSectionRef.current = props.sectionPk;
      props.onSelect?.();
    }
  };

  return (
    <div className="comment-count" onClick={handleClick}>
      <FontAwesomeIcon
        icon={faComments}
        className="comment-icon"
        data-test-id="comment-icon"
        description={helpText}
        color="grey"
      />
      <div className="tooltip">
        <span>{props.count}</span>
        <span className="tooltip-text">{helpText}</span>
      </div>
    </div>
  );
}

export class SectionNavigation extends Component {
  mapDataToNavigationTreeProps = (item, index) => {
    const wrapClassNames = [
      item.expanded ? 'expanded' : '',
      item.children.length > 0 ? 'has-children' : '',
      item.isActive ? 'active' : '',
      item.level ? 'level-' + item.level : '',
      item.iAmEditor ? 'edited' : '',
    ]
      .filter(x => !!x)
      .join(' ');

    const children = item.expanded
      ? item.children.map(this.mapDataToNavigationTreeProps)
      : [];

    const titleClassName = item.onlyTitle
      ? 'section-title only-title'
      : 'section-title';

    return (
      <li key={index} className={wrapClassNames}>
        <div className={titleClassName}>
          <Link
            to={{ hash: encodeURIComponent(item.title) }}
            onClick={item.onSelect}
            data-test-id={`${item.title.toLowerCase().replace(/ /g, '-')}-link`}
          >
            {item.title}
          </Link>
          {item.commentsCount ? (
            <CommentsCount
              count={item.commentsCount}
              onSelect={item.onSelect}
              isActive={item.isActive}
              sectionPk={item.sectionPk}
              commentsLoading={this.props.commentsLoading}
              dispatchOpenComments={this.props.dispatchOpenComments}
            />
          ) : null}
        </div>

        {item.iAmEditor ? <IAmEditorIcon /> : null}
        {children ? <ul>{children}</ul> : null}
      </li>
    );
  };

  render() {
    return (
      <>
        <Text kind="h6" className="sidebar-title">
          Table of contents
        </Text>
        <ul>{this.props.data.map(this.mapDataToNavigationTreeProps)}</ul>
      </>
    );
  }
}

export const mapStateToProps = state => {
  return {
    data: { sections: [] },
    isFetching: false,
    error: null,
    activeSectionID: 0,
    commentsLoading: !!state.viewData[COMMENTS_LIST]?.isFetching,
    newTitle: {
      data: {},
      ...state.viewData[APPLY_CHANGE_TITLE_KEY],
    },
    ...state.viewData[REPORT_DETAIL_DATA_KEY],
    ...state.viewState,
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    dispatchRequestData: (params = {}) => {
      dispatch(requestAPI(REPORT_DETAIL_DATA_KEY, params));
    },
    dispatchSelect: id => {
      dispatch(selectSection(id));
    },
    dispatchClearComments: () => {
      dispatch(clearApiKeys([COMMENTS_LIST]));
    },
    dispatchOpenComments: () => {
      dispatch(
        sendSignalsAction({
          [COMMENTS_TARGET_ID]: { [SHOW]: true },
        }),
      );
    },
  };
};

const ReportSidebar = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SidebarContainer);

export default ReportSidebar;
